import React, { useEffect, useMemo, useRef, useState } from "react";
import { isValidCustomerEmail } from "../../validation/customerEmail";
import {
  formatRubPrice,
  formatUsdtPrice,
  getMaxPurchasableQuantity,
  MAX_OTHER_STORE_QUANTITY,
} from "../helpers";
import {
  checkoutOtherStore,
  fetchOtherStoreUiConfig,
} from "../api";
import { saveRecentStoreOrder } from "../order-storage";
import { DEFAULT_SUPPORT_CONTACTS } from "../order-status";
import { getReferralPayloadForCheckout } from "../referral-storage";
import { pickCards } from "../../utils/pickCards";
import { sendWithCaptcha, useCaptcha } from "../captcha";
import CaptchaNotice from "./CaptchaNotice";
import {
  formatCodesCount,
  formatFaceAmount,
  getSumModeCards,
} from "../multiDenomination";

const CATALOG_LOADING_COUNTDOWN_START = 20;

const PAYMENT_LOADER_HTML = `<!doctype html>
<html lang="ru">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<title>Переходим к оплате…</title>
<style>
  html, body { height: 100%; margin: 0; }
  body {
    display: flex; align-items: center; justify-content: center;
    background: #111827; color: #e5e7eb;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif;
    text-align: center; padding: 24px; box-sizing: border-box;
  }
  .spinner {
    width: 48px; height: 48px; margin: 0 auto 24px;
    border: 4px solid rgba(229, 231, 235, 0.2); border-top-color: #3b82f6;
    border-radius: 50%; animation: spin 0.9s linear infinite;
  }
  h1 { font-size: 20px; font-weight: 600; margin: 0 0 8px; }
  p { font-size: 15px; color: #9ca3af; margin: 0; line-height: 1.5; }
  @keyframes spin { to { transform: rotate(360deg); } }
</style>
</head>
<body>
  <div>
    <div class="spinner"></div>
    <h1>Переходим к оплате…</h1>
    <p>Сейчас откроется страница оплаты</p>
  </div>
</body>
</html>`;

export function showPaymentLoader(payWindow) {
  try {
    payWindow.document.open();
    payWindow.document.write(PAYMENT_LOADER_HTML);
    payWindow.document.close();
  } catch {
    // Без экрана загрузки вкладка просто останется пустой до перехода на оплату
  }
}

function getUniqueDenominations(region) {
  const seen = new Set();
  return (region?.denominations || []).filter((item) => {
    if (!item?.id || seen.has(item.id)) {
      return false;
    }
    seen.add(item.id);
    return true;
  });
}

function OtherStorePurchasePanel({
  store,
  catalogLoading = false,
  catalogError = "",
  onRefreshCatalog,
  initialRegionId = "",
}) {
  const initialRegion =
    store.regions.find((item) => item.id === initialRegionId) ??
    store.regions[0];
  const initialDenomination = initialRegion?.denominations[0];

  const [regionId, setRegionId] = useState(
    initialRegion?.id ?? initialRegionId,
  );
  const [denominationId, setDenominationId] = useState(
    initialDenomination?.id ?? "",
  );
  const [quantity, setQuantity] = useState(
    getMaxPurchasableQuantity(initialDenomination?.stockAvailable ?? 0) > 0
      ? 1
      : 0,
  );
  // single — один номинал (как раньше), multi — полотно номиналов, sum — подбор по сумме
  const [mode, setMode] = useState("single");
  const [multiQuantities, setMultiQuantities] = useState({});
  const [needAmount, setNeedAmount] = useState("");
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [privacyUrl, setPrivacyUrl] = useState(
    "https://trendy-eng-shop.ru/offer#privacy",
  );
  const [paymentLifetimeMinutes, setPaymentLifetimeMinutes] = useState(5);
  const [isCryptoPayment, setIsCryptoPayment] = useState(false);
  const [testingPeriodEnabled, setTestingPeriodEnabled] = useState(false);
  const [supportContacts, setSupportContacts] = useState(null);
  const [catalogCountdown, setCatalogCountdown] = useState(
    CATALOG_LOADING_COUNTDOWN_START,
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [priceNotice, setPriceNotice] = useState("");
  const [emailHintVisible, setEmailHintVisible] = useState(false);
  const prevUnitPricesRef = useRef(null);
  const skipPriceNoticeRef = useRef(false);
  const multiListRef = useRef(null);
  const selectionRef = useRef({ regionId, denominationId });
  selectionRef.current = { regionId, denominationId };

  useEffect(() => {
    prevUnitPricesRef.current = null;
    skipPriceNoticeRef.current = false;
  }, [store.id]);

  // Каталог периодически обновляется в фоне — выбор покупателя сохраняем,
  // пока выбранный регион и номинал есть в свежем каталоге.
  useEffect(() => {
    if (!store.regions.length) {
      return;
    }

    const current = selectionRef.current;
    const region =
      store.regions.find((item) => item.id === current.regionId) ??
      store.regions[0];
    const denominations = getUniqueDenominations(region);
    const denomination =
      denominations.find((item) => item.id === current.denominationId) ??
      denominations[0];
    const keepsSelection =
      region.id === current.regionId &&
      Boolean(denomination) &&
      denomination.id === current.denominationId;
    const nextMax = getMaxPurchasableQuantity(
      denomination?.stockAvailable ?? 0,
    );

    setRegionId(region.id);
    setDenominationId(denomination?.id ?? "");
    setQuantity((currentQuantity) =>
      keepsSelection
        ? Math.min(Math.max(currentQuantity, 1), nextMax)
        : nextMax > 0
          ? 1
          : 0,
    );

    if (region.id !== current.regionId) {
      setMode("single");
      setMultiQuantities({});
      setNeedAmount("");
      return;
    }
    setMultiQuantities((currentQuantities) => {
      const next = {};
      for (const item of denominations) {
        const max = getMaxPurchasableQuantity(item.stockAvailable ?? 0);
        const value = Math.min(currentQuantities[item.id] || 0, max);
        if (value > 0) {
          next[item.id] = value;
        }
      }
      return next;
    });
  }, [store.id, store.regions]);

  useEffect(() => {
    if (consent) {
      setEmailHintVisible(false);
    }
  }, [consent]);

  useEffect(() => {
    fetchOtherStoreUiConfig()
      .then((data) => {
        if (data.privacyPolicyUrl) {
          setPrivacyUrl(data.privacyPolicyUrl);
        }
        if (data.paymentLifetimeMinutes) {
          setPaymentLifetimeMinutes(data.paymentLifetimeMinutes);
        }
        if (data.testingPeriodEnabled != null) {
          setTestingPeriodEnabled(Boolean(data.testingPeriodEnabled));
        }
        if (data.support) {
          setSupportContacts(data.support);
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!catalogLoading) {
      setCatalogCountdown(CATALOG_LOADING_COUNTDOWN_START);
      return undefined;
    }

    setCatalogCountdown(CATALOG_LOADING_COUNTDOWN_START);
    const intervalId = window.setInterval(() => {
      setCatalogCountdown((current) => (current > 0 ? current - 1 : 0));
    }, 1000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [catalogLoading]);

  const selectedRegion = useMemo(
    () => store.regions.find((region) => region.id === regionId),
    [store.regions, regionId],
  );

  const regionDenominations = useMemo(() => {
    const list = selectedRegion?.denominations || [];
    const unique = new Map();
    for (const item of list) {
      if (item?.id && !unique.has(item.id)) {
        unique.set(item.id, item);
      }
    }
    return [...unique.values()];
  }, [selectedRegion]);

  const selectedDenomination = useMemo(
    () =>
      regionDenominations.find((item) => item.id === denominationId) ?? null,
    [regionDenominations, denominationId],
  );

  const maxQuantity = getMaxPurchasableQuantity(
    selectedDenomination?.stockAvailable ?? 0,
  );

  const handleRegionChange = (event) => {
    const newRegionId = event.target.value;
    const region = store.regions.find((item) => item.id === newRegionId);
    const firstDenomination = getUniqueDenominations(region)[0] ?? null;
    const nextMax = getMaxPurchasableQuantity(
      firstDenomination?.stockAvailable ?? 0,
    );

    setRegionId(newRegionId);
    setDenominationId(firstDenomination?.id ?? "");
    setQuantity(nextMax > 0 ? 1 : 0);
    setMode("single");
    setMultiQuantities({});
    setNeedAmount("");
    setError("");
  };

  const handleDenominationChange = (event) => {
    const newDenominationId = event.target.value;
    const denomination = regionDenominations.find(
      (item) => item.id === newDenominationId,
    );
    const nextMax = getMaxPurchasableQuantity(
      denomination?.stockAvailable ?? 0,
    );

    setDenominationId(newDenominationId);
    setQuantity(nextMax > 0 ? 1 : 0);
    setError("");
  };

  const sumModeCards = useMemo(
    () => getSumModeCards(store.id, regionDenominations),
    [store.id, regionDenominations],
  );

  const needValue = Number(String(needAmount).replace(",", "."));

  const sumPlan = useMemo(() => {
    if (mode !== "sum" || !sumModeCards || !(needValue > 0)) {
      return null;
    }
    return pickCards(needValue, sumModeCards.cards, {
      maxCodes: MAX_OTHER_STORE_QUANTITY,
    });
  }, [mode, sumModeCards, needValue]);

  const orderLines = useMemo(() => {
    if (mode === "multi") {
      return regionDenominations
        .filter((item) => multiQuantities[item.id] > 0)
        .map((item) => ({ denomination: item, quantity: multiQuantities[item.id] }));
    }
    if (mode === "sum") {
      return (sumPlan?.items || [])
        .map((item) => ({
          denomination: regionDenominations.find((d) => d.id === item.id),
          quantity: item.quantity,
        }))
        .filter((line) => line.denomination);
    }
    return selectedDenomination && quantity > 0
      ? [{ denomination: selectedDenomination, quantity }]
      : [];
  }, [mode, regionDenominations, multiQuantities, sumPlan, selectedDenomination, quantity]);

  const totalCodes = orderLines.reduce((sum, line) => sum + line.quantity, 0);

  const totalPriceRub = orderLines.reduce(
    (sum, line) => sum + line.denomination.priceRub * line.quantity,
    0,
  );

  const totalPriceUsdt =
    orderLines.length > 0 &&
    orderLines.every((line) => line.denomination.priceCryptoUsdt > 0)
      ? Math.round(
          orderLines.reduce(
            (sum, line) => sum + line.denomination.priceCryptoUsdt * line.quantity,
            0,
          ) * 100,
        ) / 100
      : 0;

  const totalPrice = isCryptoPayment ? totalPriceUsdt : totalPriceRub;

  useEffect(() => {
    if (catalogLoading || orderLines.length === 0) {
      return undefined;
    }

    const nextUnitPrices = {
      key: orderLines.map((line) => line.denomination.id).join(","),
      prices: orderLines
        .map(
          (line) =>
            `${line.denomination.priceRub}/${line.denomination.priceCryptoUsdt ?? 0}`,
        )
        .join(","),
    };

    if (skipPriceNoticeRef.current) {
      skipPriceNoticeRef.current = false;
      prevUnitPricesRef.current = nextUnitPrices;
      return undefined;
    }

    const prevUnitPrices = prevUnitPricesRef.current;
    const catalogPriceChanged =
      prevUnitPrices &&
      prevUnitPrices.key === nextUnitPrices.key &&
      prevUnitPrices.prices !== nextUnitPrices.prices;

    prevUnitPricesRef.current = nextUnitPrices;

    if (!catalogPriceChanged) {
      return undefined;
    }

    setPriceNotice("Цена обновлена");
    const timerId = window.setTimeout(() => setPriceNotice(""), 4000);
    return () => window.clearTimeout(timerId);
  }, [store, catalogLoading, orderLines]);

  const hasEmail = isValidCustomerEmail(email);

  const canPurchase =
    totalCodes > 0 &&
    totalCodes <= MAX_OTHER_STORE_QUANTITY &&
    consent &&
    hasEmail &&
    !isSubmitting &&
    (isCryptoPayment ? totalPriceUsdt > 0 : totalPriceRub > 0);
  const captchaEnabled = useCaptcha(canPurchase);

  const multiFull = totalCodes >= MAX_OTHER_STORE_QUANTITY;

  const changeMultiQuantity = (item, delta) => {
    const max = getMaxPurchasableQuantity(item.stockAvailable ?? 0);
    setMultiQuantities((current) => {
      const value = (current[item.id] || 0) + delta;
      const codes = Object.values(current).reduce((sum, n) => sum + n, 0);
      if (value < 0 || value > max || (delta > 0 && codes >= MAX_OTHER_STORE_QUANTITY)) {
        return current;
      }
      const next = { ...current, [item.id]: value };
      if (value === 0) {
        delete next[item.id];
      }
      return next;
    });
    setError("");
  };

  const openMultiMode = (quantities) => {
    setMultiQuantities(quantities);
    setMode("multi");
    setError("");
  };

  const openSingleMode = () => {
    const first = orderLines[0];
    if (mode !== "single" && first) {
      setDenominationId(first.denomination.id);
      setQuantity(
        Math.min(
          first.quantity,
          getMaxPurchasableQuantity(first.denomination.stockAvailable ?? 0),
        ),
      );
    }
    setMode("single");
    setError("");
  };

  const openSumMode = () => {
    setMode("sum");
    setError("");
  };

  useEffect(() => {
    if (mode === "sum" && !sumModeCards) {
      setMode("single");
    }
  }, [mode, sumModeCards]);

  useEffect(() => {
    const list = multiListRef.current;
    if (mode !== "multi" || !list) {
      return;
    }
    const row = list.querySelector(".other-store-multi-row--active");
    if (!row) {
      return;
    }
    const listRect = list.getBoundingClientRect();
    const rowRect = row.getBoundingClientRect();
    if (rowRect.top >= listRect.top && rowRect.bottom <= listRect.bottom) {
      return;
    }
    list.scrollTop +=
      rowRect.top - listRect.top - (list.clientHeight - rowRect.height) / 2;
  }, [mode]);

  const getPurchaseButtonLabel = () => {
    if (isSubmitting) {
      return "Создаём платёж…";
    }
    if (mode !== "single" && totalCodes === 0) {
      return "Оплатить";
    }
    if (totalPrice <= 0) {
      return "Недоступно";
    }
    const priceLabel = isCryptoPayment
      ? formatUsdtPrice(totalPriceUsdt)
      : formatRubPrice(totalPriceRub);
    return hasEmail ? `Оплатить ${priceLabel}` : priceLabel;
  };

  const handleDecrease = () => {
    setQuantity((current) => Math.max(1, current - 1));
  };

  const handleIncrease = () => {
    setQuantity((current) => Math.min(maxQuantity, current + 1));
  };

  const goToOrderPage = (orderId, accessToken) => {
    const tokenQuery = accessToken
      ? `?token=${encodeURIComponent(accessToken)}`
      : "";
    window.location.href = `/store/order/${orderId}${tokenQuery}`;
  };

  const handlePurchase = async () => {
    if (!canPurchase) {
      return;
    }

    setIsSubmitting(true);
    setError("");
    setPriceNotice("");

    // После await браузер часто режет window.open (нет user gesture) —
    // вкладка оплаты не открывается, а нас уже кидает на «ожидание кода».
    // Открываем blank синхронно по клику, URL подставим после ответа API.
    let payWindow = window.open("about:blank", "_blank");
    if (payWindow) {
      // Замена noopener: страница оплаты не должна управлять нашей вкладкой
      payWindow.opener = null;
      showPaymentLoader(payWindow);
    }

    try {
      const referral = getReferralPayloadForCheckout();
      const items = orderLines.map((line) => ({
        productId: line.denomination.id,
        quantity: line.quantity,
      }));
      const payload = {
        ...(items.length === 1 ? items[0] : { items }),
        email: email.trim(),
        consent: true,
        paymentMethod: isCryptoPayment ? "crypto" : "sbp",
        expectedAmountRub: isCryptoPayment ? undefined : totalPriceRub,
        expectedAmountUsdt: isCryptoPayment ? totalPriceUsdt : undefined,
        ...referral,
      };
      const result = await sendWithCaptcha(
        (captchaToken) =>
          checkoutOtherStore(store.id, captchaToken ? { ...payload, captchaToken } : payload),
        () => {
          // Вкладка закрыла бы задание капчи; оплата откроется в этой вкладке.
          if (payWindow && !payWindow.closed) payWindow.close();
          payWindow = null;
        },
      );

      if (result.orderId) {
        saveRecentStoreOrder(store.id, result.orderId, result.accessToken);
      }

      if (result.paymentUrl) {
        if (payWindow && !payWindow.closed) {
          payWindow.location.href = result.paymentUrl;
        } else {
          // Popup всё равно заблокирован — уходим на оплату в этой вкладке
          // (returnUrl/failUrl вернут на витрину / заказ).
          window.location.href = result.paymentUrl;
          return;
        }
        if (result.orderId) {
          goToOrderPage(result.orderId, result.accessToken);
        }
        return;
      }

      if (payWindow && !payWindow.closed) {
        payWindow.close();
      }

      if (result.orderId) {
        // Заказ есть, ссылки на оплату нет — на ожидание, но с ошибкой в UI
        // не уходим молча: покажем на странице заказа через обычный PENDING.
        goToOrderPage(result.orderId, result.accessToken);
        return;
      }

      setError("Не удалось создать платёж");
    } catch (purchaseError) {
      if (payWindow && !payWindow.closed) {
        payWindow.close();
      }
      if (purchaseError?.code === "PRICE_CHANGED") {
        skipPriceNoticeRef.current = true;
        await onRefreshCatalog?.({ silent: true });
        const nextAmount =
          isCryptoPayment && purchaseError.amountUsdt != null
            ? formatUsdtPrice(purchaseError.amountUsdt)
            : formatRubPrice(purchaseError.amountRub);
        setError(`${purchaseError.message} Новая сумма: ${nextAmount}.`);
        return;
      }
      setError(
        purchaseError?.message || "Не удалось оформить заказ. Попробуйте позже.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const support = {
    ...DEFAULT_SUPPORT_CONTACTS,
    ...(supportContacts || {}),
  };

  return (
    <aside className="other-store-purchase-card">
      <h2 className="other-store-purchase-title">
        Пополнение {store.label}
      </h2>

      {catalogLoading ? (
        <p className="other-store-catalog-loading">
          <span>Обновление списка доступных карт — чуть-чуть времени</span>
          <span className="other-store-catalog-countdown" aria-hidden="true">
            {catalogCountdown}
          </span>
        </p>
      ) : null}

      {!catalogLoading && catalogError ? (
        <p className="other-store-error">{catalogError}</p>
      ) : null}

      {!catalogLoading && !catalogError && store.regions.length === 0 ? (
        <p className="other-store-description">
          Номиналы временно недоступны.
        </p>
      ) : null}

      {!catalogLoading && !catalogError && store.regions.length > 0 ? (
        <div className="other-store-purchase-form">
      <label className="other-store-field">
        <span className="other-store-field-label">Регион</span>
        <select
          className="other-store-select"
          value={regionId}
          onChange={handleRegionChange}
        >
          {store.regions.map((region) => (
            <option key={region.id} value={region.id}>
              {region.label}
            </option>
          ))}
        </select>
      </label>

      {mode === "single" ? (
        <>
          <label className="other-store-field">
            <span className="other-store-field-label">Номинал</span>
            <select
              className="other-store-select"
              value={denominationId}
              onChange={handleDenominationChange}
            >
              {regionDenominations.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.label} — {formatRubPrice(item.priceRub)}
                </option>
              ))}
            </select>
          </label>

          <div className="other-store-field">
            <span className="other-store-field-label">Количество</span>
            <div className="other-store-quantity-row">
              <div className="other-store-counter">
                <button
                  type="button"
                  className="other-store-counter-btn"
                  onClick={handleDecrease}
                  disabled={quantity <= 1}
                  aria-label="Уменьшить количество"
                >
                  −
                </button>
                <span className="other-store-counter-value">{quantity}</span>
                <button
                  type="button"
                  className="other-store-counter-btn"
                  onClick={handleIncrease}
                  disabled={quantity >= maxQuantity}
                  aria-label="Увеличить количество"
                >
                  +
                </button>
              </div>
              {selectedDenomination && (
                <span className="other-store-stock-hint">
                  {maxQuantity > 0
                    ? `Доступно: ${maxQuantity} шт.`
                    : "Нет в наличии"}
                </span>
              )}
            </div>
          </div>

          {regionDenominations.length > 1 ? (
            <div className="other-store-mode-links">
              <button
                type="button"
                className="other-store-mode-link"
                onClick={() =>
                  openMultiMode(
                    selectedDenomination && quantity > 0
                      ? { [selectedDenomination.id]: quantity }
                      : {},
                  )
                }
              >
                + Добавить номиналы
              </button>
              {sumModeCards ? (
                <button
                  type="button"
                  className="other-store-mode-link"
                  onClick={openSumMode}
                >
                  Ввести нужную сумму
                </button>
              ) : null}
            </div>
          ) : null}
        </>
      ) : null}

      {mode === "multi" ? (
        <div className="other-store-field">
          <span className="other-store-field-label">
            Сколько кодов каждого номинала
          </span>
          <ul ref={multiListRef} className="other-store-multi-list">
            {regionDenominations.map((item) => {
              const value = multiQuantities[item.id] || 0;
              const max = getMaxPurchasableQuantity(item.stockAvailable ?? 0);
              return (
                <li
                  key={item.id}
                  className={`other-store-multi-row${
                    value > 0 ? " other-store-multi-row--active" : ""
                  }`}
                >
                  <span className="other-store-multi-name">
                    <span className="other-store-multi-label">{item.label}</span>
                    <span className="other-store-multi-price">
                      {formatRubPrice(item.priceRub)}
                    </span>
                  </span>
                  {max === 0 ? (
                    <span className="other-store-stock-hint">Нет в наличии</span>
                  ) : value > 0 ? (
                    <span className="other-store-counter">
                      <button
                        type="button"
                        className="other-store-counter-btn other-store-counter-btn--sm"
                        onClick={() => changeMultiQuantity(item, -1)}
                        aria-label={`Убрать ${item.label}`}
                      >
                        −
                      </button>
                      <span className="other-store-counter-value other-store-counter-value--sm">
                        {value}
                      </span>
                      <button
                        type="button"
                        className="other-store-counter-btn other-store-counter-btn--sm"
                        onClick={() => changeMultiQuantity(item, 1)}
                        disabled={multiFull || value >= max}
                        aria-label={`Добавить ${item.label}`}
                      >
                        +
                      </button>
                    </span>
                  ) : (
                    <button
                      type="button"
                      className="other-store-multi-add"
                      onClick={() => changeMultiQuantity(item, 1)}
                      disabled={multiFull}
                      aria-label={`Добавить ${item.label}`}
                    >
                      +
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
          <div className="other-store-mode-links">
            {sumModeCards ? (
              <button
                type="button"
                className="other-store-mode-link"
                onClick={openSumMode}
              >
                Ввести нужную сумму
              </button>
            ) : null}
            <button
              type="button"
              className="other-store-mode-link"
              onClick={openSingleMode}
            >
              Один номинал
            </button>
          </div>
        </div>
      ) : null}

      {mode === "sum" && sumModeCards ? (
        <div className="other-store-field">
          <label className="other-store-field-label" htmlFor="other-store-need-amount">
            Сколько {sumModeCards.currency} нужно на балансе
          </label>
          <div className="other-store-input-wrap">
            <input
              id="other-store-need-amount"
              type="text"
              inputMode="decimal"
              className="other-store-input other-store-input--suffixed"
              value={needAmount}
              onChange={(event) =>
                setNeedAmount(event.target.value.replace(/[^\d.,]/g, ""))
              }
              placeholder="Например, 100"
              autoComplete="off"
            />
            <span className="other-store-input-suffix">{sumModeCards.currency}</span>
          </div>
          {needValue > 0 ? (
            <div className="other-store-sum-result">
              {sumPlan && sumPlan.codes > 0 ? (
                <>
                  <ul className="other-store-sum-lines">
                    {orderLines.map((line) => (
                      <li key={line.denomination.id}>
                        <span>
                          {line.denomination.label} × {line.quantity}
                        </span>
                        <span>
                          {formatRubPrice(line.denomination.priceRub * line.quantity)}
                        </span>
                      </li>
                    ))}
                  </ul>
                  <p className="other-store-sum-note">
                    {sumPlan.overpay > 0
                      ? `Получится ${formatFaceAmount(sumPlan.face, sumModeCards.currency)} — на ${formatFaceAmount(sumPlan.overpay, sumModeCards.currency)} больше: точнее из этих номиналов не собрать.`
                      : `Ровно ${formatFaceAmount(sumPlan.face, sumModeCards.currency)}.`}
                  </p>
                </>
              ) : (
                <p className="other-store-sum-note">
                  Столько кодов сейчас нет в наличии (в одном заказе до{" "}
                  {MAX_OTHER_STORE_QUANTITY} кодов). Уменьшите сумму.
                </p>
              )}
            </div>
          ) : null}
          <div className="other-store-mode-links">
            {sumPlan && sumPlan.codes > 0 ? (
              <button
                type="button"
                className="other-store-mode-link"
                onClick={() =>
                  openMultiMode(
                    Object.fromEntries(
                      orderLines.map((line) => [line.denomination.id, line.quantity]),
                    ),
                  )
                }
              >
                Изменить набор
              </button>
            ) : null}
            <button
              type="button"
              className="other-store-mode-link"
              onClick={openSingleMode}
            >
              Один номинал
            </button>
          </div>
        </div>
      ) : null}

      <div className="other-store-field">
        <label className="other-store-consent">
          <input
            type="checkbox"
            checked={consent}
            onChange={(event) => setConsent(event.target.checked)}
          />
          <span>
            Чтобы оформить заказ, нужно дать согласие{" "}
            <a
              href={privacyUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="other-store-consent-link"
              onClick={(event) => event.stopPropagation()}
            >
              на обработку персональных данных
            </a>
            . Я даю согласие.
          </span>
        </label>
      </div>

      <div className="other-store-field other-store-email-field">
        <span className="other-store-field-label">
          {totalCodes > 1 ? "Email для получения кодов" : "Email для получения кода"}
        </span>
        <div
          className="other-store-input-wrap"
          onMouseEnter={() => {
            if (!consent) {
              setEmailHintVisible(true);
            }
          }}
          onMouseLeave={() => setEmailHintVisible(false)}
        >
          <input
            type="email"
            className={`other-store-input${!consent ? " other-store-input--locked" : ""}`}
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            placeholder="example@mail.ru"
            readOnly={!consent}
            onFocus={() => {
              if (!consent) {
                setEmailHintVisible(true);
              }
            }}
            onBlur={() => setEmailHintVisible(false)}
            autoComplete="email"
          />
          {emailHintVisible && !consent ? (
            <span className="other-store-field-hint" role="status">
              Нажмите галочку выше
            </span>
          ) : null}
        </div>
      </div>

      {error ? <p className="other-store-error">{error}</p> : null}

      {priceNotice ? (
        <p className="other-store-price-notice" role="status">
          {priceNotice}
        </p>
      ) : null}

      <div className="other-store-purchase-footer">
        <button
          type="button"
          className={`other-store-buy-btn${
            isCryptoPayment ? " other-store-buy-btn-crypto" : ""
          }`}
          disabled={!canPurchase}
          onClick={handlePurchase}
        >
          {getPurchaseButtonLabel()}
        </button>

        {totalCodes > 1 ? (
          <p className="other-store-multi-summary">
            {formatCodesCount(totalCodes)} одним заказом · до{" "}
            {MAX_OTHER_STORE_QUANTITY} в заказе
          </p>
        ) : null}

        <p className="other-store-payment-hint">
          {isCryptoPayment
            ? "После нажатия «Оплатить» страница оплаты USDT откроется в новой вкладке."
            : "После нажатия «Оплатить» страница оплаты откроется в новой вкладке."}{" "}
          У вас будет {paymentLifetimeMinutes} мин. на оплату.
        </p>
        {captchaEnabled ? <CaptchaNotice /> : null}

        <label className="other-store-crypto-toggle">
          <input
            type="checkbox"
            checked={isCryptoPayment}
            onChange={(event) => setIsCryptoPayment(event.target.checked)}
          />
          <span>
            Оплатить криптовалютой
            {totalPriceUsdt > 0 ? (
              <>
                {" "}
                <span className="other-store-crypto-hint">
                  ({formatUsdtPrice(totalPriceUsdt)})
                </span>
              </>
            ) : null}
          </span>
        </label>

        {testingPeriodEnabled ? (
          <div className="other-store-testing-banner" role="status">
            <p className="other-store-testing-banner-text">
              Временно идёт тестирование нового функционала, могут встречаться
              ошибки, о которых можно сообщать в техподдержку:{" "}
              <a
                href={support.telegram}
                target="_blank"
                rel="noopener noreferrer"
                className="other-store-consent-link"
              >
                Telegram
              </a>
              {", "}
              <a
                href={support.vk}
                target="_blank"
                rel="noopener noreferrer"
                className="other-store-consent-link"
              >
                ВКонтакте
              </a>
              {", "}
              <a
                href={`mailto:${support.email}`}
                className="other-store-consent-link"
              >
                {support.email}
              </a>
            </p>
          </div>
        ) : null}
      </div>
        </div>
      ) : null}
    </aside>
  );
}

export default OtherStorePurchasePanel;
